/* eslint-disable hum/restrict-entities-imports */
/* eslint-disable hum/require-model-import */
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { MetricQueryString } from "./MetricQueryString.model";
import { Lineage } from "./Lineage.model";
import { camelCase } from "lodash";

type PeriodRange = any;

export interface Account {
    id: string;
    companyId: string;
    name: string;
    label: AccountLabel;
}

export interface MetricDescription {
    name: string;
    description: string;
    composition: "base" | "derived";
}

export interface ValueWithMeta {
    value: { number: number | null };
    meta: Lineage;
}

export type Expression =
    | { add: Expression[] }
    | { sub: Expression[] }
    | { mul: Expression[] }
    | { div: Expression[] }
    | { value: ValueWithMeta };

export type EvaluatedExpression =
    | { add: { value: ValueWithMeta; exprs: EvaluatedExpression[] } }
    | { sub: { value: ValueWithMeta; exprs: EvaluatedExpression[] } }
    | { mul: { value: ValueWithMeta; exprs: EvaluatedExpression[] } }
    | { div: { value: ValueWithMeta; exprs: EvaluatedExpression[] } }
    | { value: ValueWithMeta };

export type ContextExpression =
    | { add: { ctx: string; exprs: ContextExpression[] } }
    | { sub: { ctx: string; exprs: ContextExpression[] } }
    | { mul: { ctx: string; exprs: ContextExpression[] } }
    | { div: { ctx: string; exprs: ContextExpression[] } }
    | { value: { ctx: string; value: ValueWithMeta } };

export type EvaluatedContextExpression =
    | {
          add: {
              ctx: string;
              value: ValueWithMeta;
              exprs: EvaluatedContextExpression[];
          };
      }
    | {
          sub: {
              ctx: string;
              value: ValueWithMeta;
              exprs: EvaluatedContextExpression[];
          };
      }
    | {
          mul: {
              ctx: string;
              value: ValueWithMeta;
              exprs: EvaluatedContextExpression[];
          };
      }
    | {
          div: {
              ctx: string;
              value: ValueWithMeta;
              exprs: EvaluatedContextExpression[];
          };
      }
    | { value: { ctx: string; value: ValueWithMeta } };

export interface PlainValue {
    period: string;
    value: number;
}

export interface LineagedValue {
    period: string;
    value: number;
    expr: Expression;
}

export interface EvaluatedLineagedValue {
    period: string;
    value: number;
    expr: EvaluatedExpression;
}

export interface ContextLineagedValue {
    period: string;
    value: number;
    expr: ContextExpression;
}

export interface EvaluatedContextLineagedValue {
    period: string;
    value: number;
    expr: EvaluatedContextExpression;
}

export interface MetricQueryResponseData {
    dataAssetIds: string[];
    accounts: Record<string, Account>;
    metrics: Record<string, any[]>;
}

export interface ParsedValue {
    value: number | null;
    ref: string;
    period?: string;
    accountId?: string;
    dataAssetId?: string;
    location?: string;
    ctx?: string;
}

export interface ParsedExpression {
    period: string;
    result: number;
    formula: string;
    values: ParsedValue[];
}

export class LineagedValueWithMethods {
    constructor(
        private readonly value: LineagedValue,
        private readonly parseExprFn: (expr: Expression) => ParsedExpression,
    ) {}

    get period(): string {
        return this.value.period;
    }

    get result(): number {
        return this.value.value;
    }

    get expr(): Expression {
        return this.value.expr;
    }

    parseExpression(): ParsedExpression {
        return this.parseExprFn(this.value.expr);
    }
}

export class EvaluatedLineagedValueWithMethods {
    constructor(
        private readonly value: EvaluatedLineagedValue,
        private readonly parseExprFn: (
            expr: EvaluatedExpression,
            maxDepth?: number,
        ) => ParsedExpression,
    ) {}

    get period(): string {
        return this.value.period;
    }

    get result(): number {
        return this.value.value;
    }

    get expr(): EvaluatedExpression {
        return this.value.expr;
    }

    parseExpression(maxDepth?: number): ParsedExpression {
        return this.parseExprFn(this.value.expr, maxDepth);
    }
}

export class ContextLineagedValueWithMethods {
    constructor(
        private readonly value: ContextLineagedValue,
        private readonly parseExprFn: (
            expr: ContextExpression,
        ) => ParsedExpression,
    ) {}

    get period(): string {
        return this.value.period;
    }

    get result(): number {
        return this.value.value;
    }

    get expr(): ContextExpression {
        return this.value.expr;
    }

    parseExpression(): ParsedExpression {
        return this.parseExprFn(this.value.expr);
    }
}

export class EvaluatedContextLineagedValueWithMethods {
    constructor(
        private readonly value: EvaluatedContextLineagedValue,
        private readonly parseExprFn: (
            expr: EvaluatedContextExpression,
            maxDepth?: number,
        ) => ParsedExpression,
    ) {}

    get period(): string {
        return this.value.period;
    }

    get result(): number {
        return this.value.value;
    }

    get expr(): EvaluatedContextExpression {
        return this.value.expr;
    }

    parseExpression(maxDepth?: number): ParsedExpression {
        return this.parseExprFn(this.value.expr, maxDepth);
    }
}

export abstract class MetricQueryResponse<TValue> {
    protected readonly data: MetricQueryResponseData;
    readonly query: MetricQueryString;
    readonly periodRange?: PeriodRange;

    constructor(
        data: MetricQueryResponseData,
        query: MetricQueryString,
        periodRange?: PeriodRange,
    ) {
        this.data = data;
        this.query = query;
        this.periodRange = periodRange;
    }

    get dataAssetIds(): string[] {
        return this.data.dataAssetIds;
    }

    get accounts(): Record<string, Account> {
        return this.data.accounts;
    }

    getAccountById(id: string): Account | undefined {
        return this.data.accounts[id];
    }

    getAccountsByLabel(label: AccountLabel): Account[] {
        return Object.values(this.data.accounts).filter(
            (account) => account.label === label,
        );
    }

    getAvailableMetricKeys(): string[] {
        return Object.keys(this.data.metrics);
    }

    protected getMetricData(): any[] {
        const key = this.query.toString();
        const camelKey = camelCase(key);
        const data = this.data.metrics[camelKey];

        if (!data && Object.keys(this.data.metrics).length > 0) {
            console.warn(
                `No metric data found for query key "${key}" (camelCase: "${camelKey}"). ` +
                    `Available keys: ${Object.keys(this.data.metrics).join(", ")}`,
            );
        }

        return data || [];
    }

    getMetricDescription(): MetricDescription | undefined {
        const metricData = this.getMetricData();
        return metricData[0]?.description;
    }

    abstract getValues(): TValue[];

    abstract getValue(period: string): TValue | undefined;

    getAllPeriods(): string[] {
        return this.getValues().map((v: any) => v.period);
    }

    getValueForPeriod(period: string): number | undefined {
        const value = this.getValue(period);
        return value ? (value as any).value : undefined;
    }

    getLatestValue(): TValue | undefined {
        const values = this.getValues();
        return values[values.length - 1];
    }
}

export class PlainMetricQueryResponse extends MetricQueryResponse<PlainValue> {
    getValues(): PlainValue[] {
        const metricData = this.getMetricData();
        return metricData[0]?.values || [];
    }

    getValue(period: string): PlainValue | undefined {
        return this.getValues().find((v) => v.period === period);
    }
}

export class LineageMetricQueryResponse extends MetricQueryResponse<LineagedValue> {
    getValues(): LineagedValue[] {
        const metricData = this.getMetricData();
        return metricData[0]?.lineagedValues || [];
    }

    getValue(period: string): LineagedValue | undefined {
        return this.getValues().find((v) => v.period === period);
    }

    latest(): LineagedValueWithMethods | undefined {
        const value = super.getLatestValue();
        if (!value) return undefined;
        return new LineagedValueWithMethods(value, (expr) =>
            this.parseExpressionFromExpr(expr, value.period, value.value),
        );
    }

    getLineageForPeriod(period: string): Expression | undefined {
        const value = this.getValue(period);
        return value?.expr;
    }

    extractAllLineages(expr: Expression): Lineage[] {
        const lineages: Lineage[] = [];

        const traverse = (e: Expression): void => {
            if ("value" in e) {
                lineages.push(e.value.meta);
            } else if ("add" in e) {
                e.add.forEach(traverse);
            } else if ("sub" in e) {
                e.sub.forEach(traverse);
            } else if ("mul" in e) {
                e.mul.forEach(traverse);
            } else if ("div" in e) {
                e.div.forEach(traverse);
            }
        };

        traverse(expr);
        return lineages;
    }

    getSourceAccountsForPeriod(period: string): Account[] {
        const lineage = this.getLineageForPeriod(period);
        if (!lineage) return [];

        const lineages = this.extractAllLineages(lineage);
        const accountIds = new Set(lineages.map((l) => l.accountId));

        return Array.from(accountIds)
            .map((id) => this.getAccountById(id))
            .filter((a): a is Account => a !== undefined);
    }

    getDataAssetIdsForPeriod(period: string): string[] {
        const lineage = this.getLineageForPeriod(period);
        if (!lineage) return [];

        const lineages = this.extractAllLineages(lineage);
        return Array.from(new Set(lineages.map((l) => l.dataAssetId)));
    }

    private parseExpressionFromExpr(
        expr: Expression,
        period: string,
        result: number,
    ): ParsedExpression {
        const values: ParsedValue[] = [];

        const traverse = (node: Expression): string => {
            if ("value" in node) {
                const val = node.value;
                const index = values.length;
                values.push({
                    value: val.value.number,
                    ref: `$${index}`,
                    period: val.meta.origPeriod,
                    accountId: val.meta.accountId,
                    dataAssetId: val.meta.dataAssetId,
                    location: val.meta.loc,
                });
                return `$${index}`;
            }

            if ("add" in node) {
                const parts = node.add.map((n) => {
                    const result = traverse(n);
                    const needsParens = "sub" in n;
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" + ");
            }

            if ("sub" in node) {
                const parts = node.sub.map((n, idx) => {
                    const result = traverse(n);
                    const needsParens = idx > 0 && ("add" in n || "sub" in n);
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" - ");
            }

            if ("mul" in node) {
                const parts = node.mul.map((n) => {
                    const result = traverse(n);
                    const needsParens = "add" in n || "sub" in n;
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" * ");
            }

            if ("div" in node) {
                const parts = node.div.map((n, idx) => {
                    const result = traverse(n);
                    const needsParens =
                        "add" in n ||
                        "sub" in n ||
                        (idx > 0 && ("mul" in n || "div" in n));
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" / ");
            }

            return "";
        };

        const formula = traverse(expr);

        return {
            period,
            result,
            formula,
            values,
        };
    }

    parseExpressionForPeriod(period: string): ParsedExpression | undefined {
        const lineagedValue = this.getValue(period);
        if (!lineagedValue || !lineagedValue.expr) return undefined;

        return this.parseExpressionFromExpr(
            lineagedValue.expr,
            lineagedValue.period,
            lineagedValue.value,
        );
    }
}

export class EvaluatedLineageMetricQueryResponse extends MetricQueryResponse<EvaluatedLineagedValue> {
    getValues(): EvaluatedLineagedValue[] {
        const metricData = this.getMetricData();
        return metricData[0]?.evaledLineagedValues || [];
    }

    getValue(period: string): EvaluatedLineagedValue | undefined {
        return this.getValues().find((v) => v.period === period);
    }

    latest(): EvaluatedLineagedValueWithMethods | undefined {
        const value = super.getLatestValue();
        if (!value) return undefined;
        return new EvaluatedLineagedValueWithMethods(value, (expr, maxDepth) =>
            this.parseExpressionFromExpr(
                expr,
                value.period,
                value.value,
                maxDepth,
            ),
        );
    }

    getLineageForPeriod(period: string): EvaluatedExpression | undefined {
        const value = this.getValue(period);
        return value?.expr;
    }

    extractAllLineages(expr: EvaluatedExpression): Lineage[] {
        const lineages: Lineage[] = [];

        const traverse = (e: EvaluatedExpression): void => {
            if ("value" in e) {
                lineages.push(e.value.meta);
            } else if ("add" in e) {
                e.add.exprs.forEach(traverse);
            } else if ("sub" in e) {
                e.sub.exprs.forEach(traverse);
            } else if ("mul" in e) {
                e.mul.exprs.forEach(traverse);
            } else if ("div" in e) {
                e.div.exprs.forEach(traverse);
            }
        };

        traverse(expr);
        return lineages;
    }

    getIntermediateValue(expr: EvaluatedExpression): number | null {
        if ("value" in expr) {
            return expr.value.value.number;
        } else if ("add" in expr) {
            return expr.add.value.value.number;
        } else if ("sub" in expr) {
            return expr.sub.value.value.number;
        } else if ("mul" in expr) {
            return expr.mul.value.value.number;
        } else if ("div" in expr) {
            return expr.div.value.value.number;
        }
        return null;
    }

    private parseExpressionFromExpr(
        expr: EvaluatedExpression,
        period: string,
        result: number,
        maxDepth: number = Infinity,
    ): ParsedExpression {
        const values: ParsedValue[] = [];

        const traverse = (
            node: EvaluatedExpression,
            depth: number = 0,
        ): string => {
            if ("value" in node) {
                const val = node.value;
                const index = values.length;
                if (val.meta) {
                    values.push({
                        value: val.value.number,
                        ref: `$${index}`,
                        period: val.meta.origPeriod,
                        accountId: val.meta.accountId,
                        dataAssetId: val.meta.dataAssetId,
                        location: val.meta.loc,
                    });
                } else {
                    values.push({
                        value: val.value.number,
                        ref: `$${index}`,
                    });
                }
                return `$${index}`;
            }

            const handleOperation = (
                op: {
                    value: ValueWithMeta;
                    exprs: EvaluatedExpression[];
                },
                opSymbol: string,
            ): string => {
                if (depth >= maxDepth) {
                    const index = values.length;
                    const firstChild = op.exprs[0];
                    let childPeriod: string | undefined;

                    if ("value" in firstChild && firstChild.value.meta) {
                        childPeriod = firstChild.value.meta.origPeriod;
                    }

                    values.push({
                        value: op.value.value.number,
                        ref: `$${index}`,
                        period: childPeriod,
                    });

                    return `$${index}`;
                } else {
                    const parts = op.exprs.map((n, idx) => {
                        const result = traverse(n, depth + 1);
                        const needsParens =
                            (opSymbol === "+" && "sub" in n) ||
                            (opSymbol === "-" &&
                                idx > 0 &&
                                ("add" in n || "sub" in n)) ||
                            ((opSymbol === "*" || opSymbol === "/") &&
                                ("add" in n || "sub" in n)) ||
                            (opSymbol === "/" &&
                                idx > 0 &&
                                ("mul" in n || "div" in n));
                        return needsParens ? `(${result})` : result;
                    });
                    return parts.join(` ${opSymbol} `);
                }
            };

            if ("add" in node) {
                return handleOperation(node.add, "+");
            }

            if ("sub" in node) {
                return handleOperation(node.sub, "-");
            }

            if ("mul" in node) {
                return handleOperation(node.mul, "*");
            }

            if ("div" in node) {
                return handleOperation(node.div, "/");
            }

            return "";
        };

        const formula = traverse(expr);

        return {
            period,
            result,
            formula,
            values,
        };
    }

    parseExpressionForPeriod(
        period: string,
        maxDepth?: number,
    ): ParsedExpression | undefined {
        const lineagedValue = this.getValue(period);
        if (!lineagedValue || !lineagedValue.expr) return undefined;

        return this.parseExpressionFromExpr(
            lineagedValue.expr,
            lineagedValue.period,
            lineagedValue.value,
            maxDepth,
        );
    }
}

export class ContextLineageMetricQueryResponse extends MetricQueryResponse<ContextLineagedValue> {
    getValues(): ContextLineagedValue[] {
        const metricData = this.getMetricData();
        return metricData[0]?.ctxLineagedValues || [];
    }

    getValue(period: string): ContextLineagedValue | undefined {
        return this.getValues().find((v) => v.period === period);
    }

    latest(): ContextLineagedValueWithMethods | undefined {
        const value = super.getLatestValue();
        if (!value) return undefined;
        return new ContextLineagedValueWithMethods(value, (expr) =>
            this.parseExpressionFromExpr(expr, value.period, value.value),
        );
    }

    getLineageForPeriod(period: string): ContextExpression | undefined {
        const value = this.getValue(period);
        return value?.expr;
    }

    extractAllLineages(expr: ContextExpression): Lineage[] {
        const lineages: Lineage[] = [];

        const traverse = (e: ContextExpression): void => {
            if ("value" in e) {
                lineages.push(e.value.value.meta);
            } else if ("add" in e) {
                e.add.exprs.forEach(traverse);
            } else if ("sub" in e) {
                e.sub.exprs.forEach(traverse);
            } else if ("mul" in e) {
                e.mul.exprs.forEach(traverse);
            } else if ("div" in e) {
                e.div.exprs.forEach(traverse);
            }
        };

        traverse(expr);
        return lineages;
    }

    extractContextPaths(expr: ContextExpression): string[] {
        const contexts = new Set<string>();

        const traverse = (e: ContextExpression): void => {
            if ("value" in e) {
                contexts.add(e.value.ctx);
            } else if ("add" in e) {
                contexts.add(e.add.ctx);
                e.add.exprs.forEach(traverse);
            } else if ("sub" in e) {
                contexts.add(e.sub.ctx);
                e.sub.exprs.forEach(traverse);
            } else if ("mul" in e) {
                contexts.add(e.mul.ctx);
                e.mul.exprs.forEach(traverse);
            } else if ("div" in e) {
                contexts.add(e.div.ctx);
                e.div.exprs.forEach(traverse);
            }
        };

        traverse(expr);
        return Array.from(contexts);
    }

    getContextPathsForPeriod(period: string): string[] {
        const lineage = this.getLineageForPeriod(period);
        if (!lineage) return [];
        return this.extractContextPaths(lineage);
    }

    private parseExpressionFromExpr(
        expr: ContextExpression,
        period: string,
        result: number,
    ): ParsedExpression {
        const values: ParsedValue[] = [];

        const traverse = (node: ContextExpression): string => {
            if ("value" in node) {
                const val = node.value.value;
                const index = values.length;
                values.push({
                    value: val.value.number,
                    ref: `$${index}`,
                    period: val.meta.origPeriod,
                    accountId: val.meta.accountId,
                    dataAssetId: val.meta.dataAssetId,
                    location: val.meta.loc,
                    ctx: node.value.ctx,
                });
                return `$${index}`;
            }

            if ("add" in node) {
                const parts = node.add.exprs.map((n) => {
                    const result = traverse(n);
                    const needsParens = "sub" in n;
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" + ");
            }

            if ("sub" in node) {
                const parts = node.sub.exprs.map((n, idx) => {
                    const result = traverse(n);
                    const needsParens = idx > 0 && ("add" in n || "sub" in n);
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" - ");
            }

            if ("mul" in node) {
                const parts = node.mul.exprs.map((n) => {
                    const result = traverse(n);
                    const needsParens = "add" in n || "sub" in n;
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" * ");
            }

            if ("div" in node) {
                const parts = node.div.exprs.map((n, idx) => {
                    const result = traverse(n);
                    const needsParens =
                        "add" in n ||
                        "sub" in n ||
                        (idx > 0 && ("mul" in n || "div" in n));
                    return needsParens ? `(${result})` : result;
                });
                return parts.join(" / ");
            }

            return "";
        };

        const formula = traverse(expr);

        return {
            period,
            result,
            formula,
            values,
        };
    }

    parseExpressionForPeriod(period: string): ParsedExpression | undefined {
        const lineagedValue = this.getValue(period);
        if (!lineagedValue || !lineagedValue.expr) return undefined;

        return this.parseExpressionFromExpr(
            lineagedValue.expr,
            lineagedValue.period,
            lineagedValue.value,
        );
    }
}

export class EvaluatedContextLineageMetricQueryResponse extends MetricQueryResponse<EvaluatedContextLineagedValue> {
    getValues(): EvaluatedContextLineagedValue[] {
        const metricData = this.getMetricData();
        return metricData[0]?.evaledCtxLineagedValues || [];
    }

    getValue(period: string): EvaluatedContextLineagedValue | undefined {
        return this.getValues().find((v) => v.period === period);
    }

    latest(): EvaluatedContextLineagedValueWithMethods | undefined {
        const value = super.getLatestValue();
        if (!value) return undefined;
        return new EvaluatedContextLineagedValueWithMethods(
            value,
            (expr, maxDepth) =>
                this.parseExpressionFromExpr(
                    expr,
                    value.period,
                    value.value,
                    maxDepth,
                ),
        );
    }

    getLineageForPeriod(
        period: string,
    ): EvaluatedContextExpression | undefined {
        const value = this.getValue(period);
        return value?.expr;
    }

    extractAllLineages(expr: EvaluatedContextExpression): Lineage[] {
        const lineages: Lineage[] = [];

        const traverse = (e: EvaluatedContextExpression): void => {
            if ("value" in e) {
                lineages.push(e.value.value.meta);
            } else if ("add" in e) {
                e.add.exprs.forEach(traverse);
            } else if ("sub" in e) {
                e.sub.exprs.forEach(traverse);
            } else if ("mul" in e) {
                e.mul.exprs.forEach(traverse);
            } else if ("div" in e) {
                e.div.exprs.forEach(traverse);
            }
        };

        traverse(expr);
        return lineages;
    }

    extractContextPaths(expr: EvaluatedContextExpression): string[] {
        const contexts = new Set<string>();

        const traverse = (e: EvaluatedContextExpression): void => {
            if ("value" in e) {
                contexts.add(e.value.ctx);
            } else if ("add" in e) {
                contexts.add(e.add.ctx);
                e.add.exprs.forEach(traverse);
            } else if ("sub" in e) {
                contexts.add(e.sub.ctx);
                e.sub.exprs.forEach(traverse);
            } else if ("mul" in e) {
                contexts.add(e.mul.ctx);
                e.mul.exprs.forEach(traverse);
            } else if ("div" in e) {
                contexts.add(e.div.ctx);
                e.div.exprs.forEach(traverse);
            }
        };

        traverse(expr);
        return Array.from(contexts);
    }

    getIntermediateValue(expr: EvaluatedContextExpression): number | null {
        if ("value" in expr) {
            return expr.value.value.value.number;
        } else if ("add" in expr) {
            return expr.add.value.value.number;
        } else if ("sub" in expr) {
            return expr.sub.value.value.number;
        } else if ("mul" in expr) {
            return expr.mul.value.value.number;
        } else if ("div" in expr) {
            return expr.div.value.value.number;
        }
        return null;
    }

    getContextPathsForPeriod(period: string): string[] {
        const lineage = this.getLineageForPeriod(period);
        if (!lineage) return [];
        return this.extractContextPaths(lineage);
    }

    private parseExpressionFromExpr(
        expr: EvaluatedContextExpression,
        period: string,
        result: number,
        maxDepth: number = Infinity,
    ): ParsedExpression {
        const values: ParsedValue[] = [];

        const traverse = (
            node: EvaluatedContextExpression,
            depth: number = 0,
        ): string => {
            if ("value" in node) {
                const val = node.value.value;
                const index = values.length;
                if (val.meta) {
                    values.push({
                        value: val.value.number,
                        ref: `$${index}`,
                        period: val.meta.origPeriod,
                        accountId: val.meta.accountId,
                        dataAssetId: val.meta.dataAssetId,
                        location: val.meta.loc,
                        ctx: node.value.ctx,
                    });
                } else {
                    values.push({
                        value: val.value.number,
                        ref: `$${index}`,
                        ctx: node.value.ctx,
                    });
                }
                return `$${index}`;
            }

            const handleOperation = (
                op: {
                    ctx: string;
                    value: ValueWithMeta;
                    exprs: EvaluatedContextExpression[];
                },
                opSymbol: string,
            ): string => {
                if (depth >= maxDepth) {
                    const index = values.length;
                    const firstChild = op.exprs[0];
                    let childPeriod: string | undefined;

                    if ("value" in firstChild && firstChild.value.value.meta) {
                        childPeriod = firstChild.value.value.meta.origPeriod;
                    }

                    values.push({
                        value: op.value.value.number,
                        ref: `$${index}`,
                        period: childPeriod,
                        ctx: op.ctx,
                    });

                    return `$${index}`;
                } else {
                    const parts = op.exprs.map((n, idx) => {
                        const result = traverse(n, depth + 1);
                        const needsParens =
                            (opSymbol === "+" && "sub" in n) ||
                            (opSymbol === "-" &&
                                idx > 0 &&
                                ("add" in n || "sub" in n)) ||
                            ((opSymbol === "*" || opSymbol === "/") &&
                                ("add" in n || "sub" in n)) ||
                            (opSymbol === "/" &&
                                idx > 0 &&
                                ("mul" in n || "div" in n));
                        return needsParens ? `(${result})` : result;
                    });
                    return parts.join(` ${opSymbol} `);
                }
            };

            if ("add" in node) {
                return handleOperation(node.add, "+");
            }

            if ("sub" in node) {
                return handleOperation(node.sub, "-");
            }

            if ("mul" in node) {
                return handleOperation(node.mul, "*");
            }

            if ("div" in node) {
                return handleOperation(node.div, "/");
            }

            return "";
        };

        const formula = traverse(expr);

        return {
            period,
            result,
            formula,
            values,
        };
    }

    parseExpressionForPeriod(
        period: string,
        maxDepth?: number,
    ): ParsedExpression | undefined {
        const lineagedValue = this.getValue(period);
        if (!lineagedValue || !lineagedValue.expr) return undefined;

        return this.parseExpressionFromExpr(
            lineagedValue.expr,
            lineagedValue.period,
            lineagedValue.value,
            maxDepth,
        );
    }
}

export enum ValueKind {
    PLAIN = "plain",
    LINEAGE = "lineage",
    EVALED_LINEAGE = "evaled_lineage",
    CTX_LINEAGE = "ctx_lineage",
    EVALED_CTX_LINEAGE = "evaled_ctx_lineage",
}

export type MetricQueryResponseType =
    | PlainMetricQueryResponse
    | LineageMetricQueryResponse
    | EvaluatedLineageMetricQueryResponse
    | ContextLineageMetricQueryResponse
    | EvaluatedContextLineageMetricQueryResponse;

export function createMetricQueryResponse(
    rawData: any,
    query: MetricQueryString,
    valueKind: ValueKind,
    periodRange?: PeriodRange,
): MetricQueryResponseType {
    const data: MetricQueryResponseData = {
        dataAssetIds: rawData.data_asset_ids || rawData.dataAssetIds || [],
        accounts: rawData.accounts || {},
        metrics: rawData.metrics || {},
    };

    switch (valueKind) {
        case ValueKind.PLAIN:
            return new PlainMetricQueryResponse(data, query, periodRange);
        case ValueKind.LINEAGE:
            return new LineageMetricQueryResponse(data, query, periodRange);
        case ValueKind.EVALED_LINEAGE:
            return new EvaluatedLineageMetricQueryResponse(
                data,
                query,
                periodRange,
            );
        case ValueKind.CTX_LINEAGE:
            return new ContextLineageMetricQueryResponse(
                data,
                query,
                periodRange,
            );
        case ValueKind.EVALED_CTX_LINEAGE:
            return new EvaluatedContextLineageMetricQueryResponse(
                data,
                query,
                periodRange,
            );
        default:
            return new PlainMetricQueryResponse(data, query, periodRange);
    }
}
